import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Settings, LogOut, Library, BrainCircuit, Bell, LogIn } from 'lucide-react'
// import { cn } from '@/lib/utils'
import useAuthStore from '@/store/useAuthStore'
import { buildAvatarUrlClient } from '@/lib/avatarClient'
import useNotificationStore from '@/store/useNotificationStore'
import { websocketService } from '@/lib/websocketService'

export default function ProfileMenu() {
    const [isOpen, setIsOpen] = useState(false)

    const { user, isAuthenticated, logout } = useAuthStore();
    const { unreadCount, fetchNotifications, notifications, isLoading, error } = useNotificationStore();
    const navigate = useNavigate();

    // Connect WebSocket and fetch notifications on mount
    useEffect(() => {
        if (isAuthenticated) {
            // Connect to WebSocket for real-time notifications
            websocketService.connect();

            // Fetch initial notifications
            fetchNotifications(50, 0);

            // WebSocket handles realtime updates, no need for polling
            // const refreshInterval = setInterval(() => {
            //     fetchNotifications(50, 0);
            // }, 60000); // Refresh every minute

            // return () => {
            //     clearInterval(refreshInterval);
            // };
        }
    }, [isAuthenticated, fetchNotifications]);

    // Force re-render when any notification store state changes
    // This ensures the component updates when new notifications arrive via WebSocket
    useEffect(() => {
        // This effect runs whenever any of these store values change
        // The component will re-render automatically due to React's state update mechanism
    }, [unreadCount, notifications, isLoading, error]);

    // Clean up WebSocket connection on unmount
    useEffect(() => {
        return () => {
            // Only disconnect if we're not navigating to another page
            // The WebSocket will be reused by other components
        };
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
        setIsOpen(false);
    };

    // Define menu items based on authentication status
    const menuItems = isAuthenticated
        ? [
            { name: 'Library', href: '/library', icon: Library },
            { name: 'Beta', href: '/beta-testing', icon: BrainCircuit },
            { name: 'Settings', href: '/settings', icon: Settings },
            {
                name: 'Sign out',
                icon: LogOut,
                onClick: handleLogout,
                href: '#'
            },
        ]
        : [
            { name: 'Sign in', href: '/login', icon: LogIn },
        ];

    // Return early if not authenticated
    if (!isAuthenticated) {
        return (
            <Link
                to="/login"
                className="flex items-center rounded-lg p-1.5 sm:p-2 text-zinc-400 hover:text-white hover:bg-zinc-800/30 gap-2"
            >
                <LogIn className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden sm:inline text-sm">Sign In</span>
            </Link>
        );
    }

    return (
        <div
            className="relative flex items-center"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
        >
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        className="absolute right-full flex items-center gap-3 pr-4"
                    >
                        {/* Notifications */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 25,
                                delay: 0,
                            }}
                        >
                            <Link
                                to="/notifications"
                                className="relative flex items-center justify-center w-10 h-10 text-zinc-400 hover:text-white"
                            >
                                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-[rgb(var(--accent-1))] flex items-center justify-center">
                                        <span className="absolute h-full w-full rounded-full bg-[rgb(var(--accent-1))] animate-ping" />
                                        <span className="relative text-[8px] font-medium text-white">
                                            {unreadCount > 9 ? '9+' : unreadCount}
                                        </span>
                                    </span>
                                )}
                            </Link>
                        </motion.div>

                        {/* Menu Items */}
                        {menuItems.map((item, index) => (
                            <motion.div
                                key={item.name}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 300,
                                    damping: 25,
                                    delay: (index + 1) * 0.05,
                                }}
                            >
                                {item.onClick ? (
                                    <button
                                        onClick={item.onClick}
                                        className="flex items-center justify-center w-10 h-10 text-zinc-400 hover:text-white cursor-pointer"
                                    >
                                        <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                                    </button>
                                ) : (
                                    <Link
                                        to={item.href}
                                        className="flex items-center justify-center w-10 h-10 text-zinc-400 hover:text-white"
                                    >
                                        <item.icon className="h-4 w-4 sm:h-5 sm:w-5" />
                                    </Link>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            <Link to="/profile" className="flex items-center rounded-lg p-1.5 sm:p-2 text-zinc-400 hover:text-white hover:bg-zinc-800/30">
                <div className="relative h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-zinc-800 overflow-hidden">
                    {user?.avatar ? (
                        <img src={buildAvatarUrlClient(user.avatar) || `https://ui-avatars.com/api/?name=${user.username}&size=128&background=1f2937&color=ffffff&bold=true`} alt={user.username} className="h-full w-full object-cover" />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-white">
                            <img src={`https://ui-avatars.com/api/?name=${user.username}&size=128&background=1f2937&color=ffffff&bold=true`} alt={user.username} className="h-full w-full object-cover" />
                        </div>
                    )}
                </div>
                <span className="ml-2 text-sm font-medium hidden sm:block truncate max-w-[80px]">
                    {user?.username || ''}
                </span>
            </Link>
        </div>
    )
} 